
import React, { useState, useEffect } from 'react';
import { BookModel, MemberModel, ScheduleEvent, BranchModel } from '../../../../models';
import { Settings2, X, Plus, Calendar as CalendarIcon, BookOpen, Layers, Files, GraduationCap, Clock, Building2, CheckCircle2, Search, ArrowRight } from 'lucide-react';

interface ScheduleSettingProps {
  book: BookModel | null;
  selectedMemberIds: string[];
  members: MemberModel[];
  branches?: BranchModel[];
  books?: BookModel[]; // Add books prop to allow selection
  startDate?: string;
  endDate?: string;
  onClose: () => void;
  onGenerate: (events: ScheduleEvent[]) => void;
}

const ScheduleSetting: React.FC<ScheduleSettingProps> = ({
  book: initialBook,
  selectedMemberIds,
  members,
  branches,
  books = [],
  startDate: initialStartDate,
  endDate: initialEndDate,
  onClose,
  onGenerate
}) => {
  // Book Selection State
  const [selectedBook, setSelectedBook] = useState<BookModel | null>(initialBook);
  const [bookSearch, setBookSearch] = useState('');

  // Rules State
  const [unitType, setUnitType] = useState<'chapters' | 'pages' | 'questions' | 'days'>('chapters');
  const [startIndex, setStartIndex] = useState(1);
  const [dailyLoad, setDailyLoad] = useState(1);
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri default
  const [startDate, setStartDate] = useState(initialStartDate || new Date().toISOString().split('T')[0]);

  // Calculate duration based on diff between startDate and endDate if provided
  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      // If we have a range, we might want to adjust dailyLoad or just respect the range
      // For now, we trust the user's manual input for dailyLoad but we set the startDate
      setStartDate(initialStartDate);
    }
  }, [initialStartDate, initialEndDate]);

  const filteredBooks = books.filter(b =>
    b.title.toLowerCase().includes(bookSearch.toLowerCase()) ||
    b.subject.toLowerCase().includes(bookSearch.toLowerCase())
  );

  const toggleDay = (day: number) => {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleGenerate = () => {
    if (!selectedBook) return alert('교재를 선택해주세요.');

    const maxVal = selectedBook[unitType] || 0;
    if (startIndex > maxVal) return alert('시작 번호가 전체 분량을 초과합니다.');
    if (dailyLoad <= 0) return alert('하루 학습량을 1 이상으로 입력하세요.');
    if (selectedDays.length === 0) return alert('학습 요일을 선택하세요.');

    const newEvents: ScheduleEvent[] = [];
    const [y, m, d] = startDate.split('-').map(Number);

    // Calculate End Date Limit if range was provided
    let limitDate: number | null = null;
    if (initialEndDate) {
      const [ly, lm, ld] = initialEndDate.split('-').map(Number);
      limitDate = new Date(ly, lm - 1, ld, 23, 59, 59).getTime();
    }

    selectedMemberIds.forEach(mId => {
      const member = members.find(m => m.id === mId);
      if (!member) return;

      let currentVal = startIndex;
      let dateCursor = new Date(y, m - 1, d);
      let dayIndex = 0;

      while (currentVal <= maxVal) {
        // Find next valid day
        while (!selectedDays.includes(dateCursor.getDay())) {
          dateCursor.setDate(dateCursor.getDate() + 1);
        }

        // If we exceed the user-selected range (if provided), stop generating?
        // OR we just generate until book is finished.
        // User request "Assign using selected period" implies we should probably try to fit it or warn.
        // For now, let's just generate until book is done, but if strictly adhering to range is needed, we'd break here.
        // Let's implement a soft break if date exceeds strict range, or just let it flow. 
        // Given "Assign schedule from period", usually means "Fill this period". 
        // But the logic here is "Book driven". 
        // Let's stick to Book Driven for now as it's safer, but respect start date.

        // *Correction*: User said "Choose period on calendar and give schedule". 
        // Use case: "Study Page 10-20 from Jan 1 to Jan 5".
        // Current logic: "Start from Page 10, do 2 pages/day".

        // If limitDate is set, we check against it.
        if (limitDate && dateCursor.getTime() > limitDate) {
          break;
        }

        const unitStart = currentVal;
        const unitEnd = Math.min(currentVal + dailyLoad - 1, maxVal);
        let unitLabel = '';
        let taskText = '';

        if (unitType === 'chapters') {
          unitLabel = '단원';
          taskText = `${unitLabel} ${unitStart} ~ ${unitEnd}`;
        } else if (unitType === 'pages') {
          unitLabel = '쪽';
          taskText = `${unitLabel} ${unitStart} ~ ${unitEnd}`;
        } else if (unitType === 'questions') {
          unitLabel = '문항';
          taskText = `${unitLabel} ${unitStart} ~ ${unitEnd}`;
        } else if (unitType === 'days') {
          unitLabel = 'Day';
          taskText = unitStart === unitEnd ? `Day ${unitStart}` : `Day ${unitStart} ~ ${unitEnd}`;
        }

        newEvents.push({
          id: crypto.randomUUID(), // Generate a temp ID
          bookId: selectedBook.id,
          bookTitle: selectedBook.title,
          subject: selectedBook.subject,
          memberId: mId,
          memberName: member.name,
          branchId: member.branchId,
          date: dateCursor.getTime(),
          task: taskText,
          unitType,
          startVal: unitStart,
          endVal: unitEnd,
          taskIndex: dayIndex,
          status: 'pending'
        });

        currentVal = unitEnd + 1;
        dateCursor.setDate(dateCursor.getDate() + 1);
        dayIndex++;
      }
    });

    onGenerate(newEvents);
  };

  const getSubjectColor = (subject: string) => {
    const colors: Record<string, string> = {
      '국어': 'bg-rose-500', '수학': 'bg-blue-600', '영어': 'bg-indigo-600',
      '한국사': 'bg-amber-700', '사회': 'bg-orange-600', '과학': 'bg-emerald-600',
    };
    return colors[subject] || 'bg-slate-600';
  };

  // 1. If no book selected, show Book Selection UI
  if (!selectedBook) {
    return (
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[85vh]">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
            <div>
              <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
                <BookOpen size={20} className="text-indigo-600" /> 교재 선택
              </h3>
              <p className="text-xs font-bold text-slate-400 mt-1">일정을 생성할 교재를 선택해주세요.</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 bg-slate-50 border-b border-slate-100 sticky top-[80px] z-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="교재명, 과목, 출판사 검색..."
                value={bookSearch}
                onChange={e => setBookSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl border-none shadow-sm focus:ring-2 focus:ring-indigo-500 font-bold text-sm bg-white"
                autoFocus
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
            {filteredBooks.map(b => (
              <button
                key={b.id}
                onClick={() => setSelectedBook(b)}
                className="w-full text-left bg-white p-3 rounded-xl border border-slate-100 shadow-sm hover:border-indigo-400 hover:shadow-md transition-all flex gap-4 group"
              >
                <div className="w-12 h-16 bg-slate-50 rounded-lg flex items-center justify-center shrink-0 border border-slate-100 overflow-hidden">
                  {b.coverUrl ? (
                    <img src={b.coverUrl} className="w-full h-full object-contain" />
                  ) : (
                    <span className={`text-[10px] font-black ${getSubjectColor(b.subject).replace('bg-', 'text-')}`}>{b.subject.substring(0, 2)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0 py-0.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded text-white font-black ${getSubjectColor(b.subject)}`}>{b.subject}</span>
                    <span className="text-[9px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-bold">{b.type}</span>
                  </div>
                  <h4 className="font-bold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">{b.title}</h4>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400 font-medium">
                    <span>{b.publisher}</span>
                    <span>•</span>
                    <span>{b.grade}</span>
                  </div>
                </div>
                <div className="flex items-center justify-center px-2 text-slate-300 group-hover:text-indigo-500 transition-colors">
                  <ArrowRight size={18} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 2. Main Rule UI (once book is selected)
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col md:flex-row h-auto max-h-[90vh]">

        {/* Left Panel: Book Details */}
        <div className="md:w-2/5 bg-slate-50 p-8 border-r border-slate-100 flex flex-col space-y-8 overflow-y-auto custom-scrollbar">
          <button onClick={() => setSelectedBook(null)} className="flex items-center gap-2 text-slate-400 hover:text-indigo-600 transition-colors font-bold text-xs mb-[-10px]">
            <ArrowRight className="rotate-180" size={14} /> 교재 다시 선택하기
          </button>

          <div className="flex items-center gap-2 text-indigo-600 font-black uppercase tracking-widest text-xs">
            <BookOpen size={16} />
            <span>선택된 교재 정보</span>
          </div>

          <div className="flex flex-col items-center">
            <div className="w-48 h-64 bg-white rounded-2xl shadow-xl border border-slate-100 flex items-center justify-center overflow-hidden mb-6 relative">
              {selectedBook.coverUrl ? (
                <img src={selectedBook.coverUrl} alt={selectedBook.title} className="w-full h-full object-contain p-2" />
              ) : (
                <div className={`w-full h-full flex flex-col items-center justify-center text-white ${getSubjectColor(selectedBook.subject)}`}>
                  <BookOpen size={48} className="opacity-30 mb-2" />
                  <span className="font-black text-xl">{selectedBook.subject}</span>
                </div>
              )}
              <div className="absolute top-4 left-4">
                <span className={`px-2 py-1 rounded-lg text-[10px] font-black text-white shadow-lg ${getSubjectColor(selectedBook.subject)}`}>
                  {selectedBook.subject}
                </span>
              </div>
            </div>

            <h3 className="text-2xl font-black text-slate-800 text-center leading-tight mb-2 px-4">
              {selectedBook.title}
            </h3>
            <div className="flex items-center gap-2 text-slate-400 font-bold text-sm">
              <Building2 size={14} />
              <span>{selectedBook.publisher}</span>
              <span className="mx-1 opacity-30">•</span>
              <span>{selectedBook.grade}</span>
            </div>
          </div>

        </div>

        {/* Right Panel: Settings Form */}
        <div className="md:w-3/5 p-10 flex flex-col relative overflow-y-auto custom-scrollbar">
          <button onClick={onClose} className="absolute right-8 top-8 hover:bg-slate-100 p-2 rounded-full transition-colors text-slate-400">
            <X size={24} />
          </button>

          <div className="mb-10">
            <h3 className="text-3xl font-black text-slate-800 flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-100">
                <Settings2 size={24} />
              </div>
              학습 규칙 설정
            </h3>
            <p className="text-slate-400 font-medium ml-15">교재 진행 방식과 학습 주기를 설정해주세요.</p>
          </div>

          <div className="space-y-8 flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Unit Type Selection */}
              <div className="space-y-3">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-indigo-500" /> 학습 기준 단위
                </label>
                <div className="flex bg-slate-100 p-1.5 rounded-xl">
                  {(['chapters', 'pages', 'questions', 'days'] as const).map(u => (
                    <button
                      key={u} onClick={() => setUnitType(u)}
                      className={`flex-1 py-3 rounded-lg text-xs font-black transition-all ${unitType === u ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                      {u === 'chapters' ? '단원' : u === 'pages' ? '쪽' : u === 'questions' ? '문항' : '일'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Members Display */}
              <div className="space-y-3">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-indigo-500" /> 학습 대상 학생 (총 {selectedMemberIds.length}명)
                </label>
                <div className="bg-white border border-slate-200 rounded-lg max-h-40 overflow-y-auto custom-scrollbar">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr className="text-[10px] font-black text-slate-500 uppercase">
                        <th className="px-3 py-2 border-b border-slate-100">지점</th>
                        <th className="px-3 py-2 border-b border-slate-100">이름</th>
                        <th className="px-3 py-2 border-b border-slate-100">학교</th>
                        <th className="px-3 py-2 border-b border-slate-100">학년</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedMemberIds.map(id => {
                        const m = members.find(mem => mem.id === id);
                        if (!m) return null;
                        const branchName = branches?.find(b => b.id === m.branchId)?.name || '-';
                        return (
                          <tr key={id} className="text-xs hover:bg-slate-50 transition-colors">
                            <td className="px-3 py-2 font-medium text-slate-500">{branchName}</td>
                            <td className="px-3 py-2 font-bold text-slate-800">{m.name}</td>
                            <td className="px-3 py-2 text-slate-500">{m.schoolName || '-'}</td>
                            <td className="px-3 py-2 text-slate-500">{m.grade}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">시작 지점 (번호/쪽/Day)</label>
                <input
                  type="number" value={startIndex} onChange={e => setStartIndex(Number(e.target.value))}
                  className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-5 py-4 text-base font-bold outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-inner"
                />
              </div>
              <div className="space-y-3">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">하루 권장 학습량</label>
                <input
                  type="number" value={dailyLoad} onChange={e => setDailyLoad(Number(e.target.value))}
                  className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-5 py-4 text-base font-bold outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-inner"
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest">주간 학습 요일 (복수 선택)</label>
              <div className="grid grid-cols-7 gap-3">
                {['일', '월', '화', '수', '목', '금', '토'].map((label, idx) => (
                  <button
                    key={idx} onClick={() => toggleDay(idx)}
                    className={`w-full aspect-square rounded-xl text-sm font-black border-2 transition-all flex items-center justify-center ${selectedDays.includes(idx)
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-100 scale-105'
                      : 'bg-white border-slate-100 text-slate-400 hover:border-indigo-200'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <CalendarIcon size={14} className="text-indigo-500" /> 학습 시작일 {initialEndDate && `~ 종료일 (범위 지정됨)`}
                </label>
                {initialEndDate && (
                  <span className="text-[10px] font-bold text-slate-400">
                    {initialEndDate} 까지의 일정만 생성됩니다.
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="flex-1 bg-slate-50 border-2 border-slate-100 rounded-xl px-6 py-4 text-base font-bold outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-inner"
                />
                {initialEndDate && (
                  <>
                    <ArrowRight size={20} className="text-slate-300" />
                    <div className="flex-1 bg-indigo-50/50 border-2 border-indigo-100/50 rounded-xl px-6 py-4 text-slate-500 font-bold opacity-80 cursor-not-allowed text-center">
                      {initialEndDate}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="pt-8 flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 bg-slate-100 text-slate-500 py-5 rounded-full font-black text-sm hover:bg-slate-200 transition-all active:scale-95"
              >
                설정 취소
              </button>
              <button
                onClick={handleGenerate}
                className="flex-[2] bg-indigo-600 text-white py-5 rounded-full font-black text-sm shadow-2xl shadow-indigo-100 hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <Plus size={20} strokeWidth={3} />
                스케줄 일괄 생성 및 저장
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSetting;
